/* eslint-disable react-hooks/exhaustive-deps */
import { bracketOption } from '@/commonFunctions/bracketActions'
import { Button, ButtonPrimary } from '@/components/button'
import { Checkmark, Close, FilterNoBg, InfoIconOutline } from '@/components/icons'
import { ConfirmModal, NotifyModal } from '@/components/modal'
import { PlayerItemLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import {
  useBracketCreateTeam,
  useBracketSwitchPlayers,
  useBracketWithdraw,
  useGetBracketUnmatchedPlayers,
  useGetClubStaff,
} from '@/services'
import { BracketPlayer, Order, PayloadBracketCreateTeam } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import { isEmpty } from 'lodash'
import { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { eventSelector } from '../selectors'
import { BracketPlayerItem } from './BracketPlayerItem'
import { SelectBracketModal } from './SelectBracketModal'
import { WithdrawModal } from './WithdrawModal'
import { WithdrawRefundModal } from './WithdrawRefundModal'

type OpenSwitchBracketType = 'STEP1' | 'STEP2' | ''
type BracketPlayerClone = BracketPlayer & { failedReason?: string }

export const BracketUnmatchedPlayers = () => {
  const { t } = useTranslation()

  const params = useParams()
  const { token } = useAppSelector(authSelector)
  const { bracket, event } = useAppSelector(eventSelector)
  const isMobile = useMediaQuery('(max-width: 767px)')

  const { eventId, bracketId } = params

  const { mutate: getUnmatchedPlayers, data: resUnmatchedPlayers, isLoading } = useGetBracketUnmatchedPlayers()
  const { mutate: switchBrackets, data: resBracketSwitchPlayers } = useBracketSwitchPlayers()
  const { mutate: bracketCreateTeam } = useBracketCreateTeam()
  const { mutate: getClubStaff, data: resGetClubStaff } = useGetClubStaff()
  const { mutate: bracketWithdraw } = useBracketWithdraw()

  const [offset, setOffset] = useState(OFFSET)
  const [unmatchedPlayers, setUnmatchedPlayers] = useState<BracketPlayerClone[]>([])
  const [order, setOrder] = useState<Order | ''>()
  const [supportModal, setSupportModal] = useState(false)
  const [switchBracketData, setSwitchBracketData] = useState<any>([])
  const [switchBracket, setSwitchBracket] = useState<OpenSwitchBracketType>('')
  const [targetBracket, setTargetBracket] = useState<any>()
  const [availableBrackets, setAvailableBrackets] = useState<any>([])
  const [team, setTeam] = useState<BracketPlayer[]>([])
  const [mixedBracketError, setMixedBracketError] = useState(false)
  const [waitListError, setWaitlistBracketError] = useState(false)
  const [createTeam, setCreateTeam] = useState(false)
  const [withdrawRefund, setWithdrawRefund] = useState<BracketPlayer | null>(null)
  const [withdraw, setWithdraw] = useState<BracketPlayer | null>(null)
  const [lastElement, setLastElement] = useState<any>(null)

  const openCreateTeam = () => setCreateTeam(true)

  const closeCreateTeam = () => setCreateTeam(false)

  const openWithdraw = (player: BracketPlayer) => setWithdraw(player)

  const closeWithDraw = () => setWithdraw(null)

  const openWithdrawRefund = (player: BracketPlayer) => setWithdrawRefund(player)

  const closeWithdrawRefund = () => setWithdrawRefund(null)

  const openSupportModal = () => setSupportModal(true)

  const closeSupportModal = () => setSupportModal(false)

  const openSwitchBracketModal = (step: OpenSwitchBracketType) => setSwitchBracket(step)

  const closeSwitchBracketModal = () => {
    setSwitchBracket('')
    setTargetBracket(null)
  }

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const updateTeam = (player: BracketPlayer) => {
    const checkPlayer = team.find((item) => item.id === player.id) || []
    if (isEmpty(checkPlayer)) {
      setTeam([...team, player])
    } else {
      const teamsData = team.filter((item) => {
        return item.id !== player.id
      })
      setTeam(teamsData)
    }
  }

  const switchUnmatchedPlayerBracket = (player: BracketPlayer) => {
    const checkPlayer = switchBracketData.find((item: any) => item.playerId.toString() === player.id.toString()) || []
    if (isEmpty(checkPlayer) && bracket) {
      setSwitchBracketData([
        ...switchBracketData,
        {
          clubId: bracket?.clubId,
          eventId,
          eventName: bracket.leagueName,
          playerId: player.id.toString(),
          reSeedBracket: true,
          sourceBracketId: bracketId,
          sourceBracketName: bracket.name,
          targetBracketId: '',
          targetBracketName: '',
          gender: player.gender,
          age: player.age,
        },
      ])
    } else {
      const switchData = switchBracketData.filter((item: any) => {
        return item.playerId.toString() !== player.id.toString()
      })
      setSwitchBracketData(switchData)
    }
  }

  const onChangeOrder = (type: Order) => {
    if (order !== type || offset !== OFFSET) {
      setOrder(type)
      setUnmatchedPlayers([])
      setOffset(OFFSET)
    }
  }

  const onClearFilter = () => {
    if (order !== '' || offset !== OFFSET) {
      setOrder('')
      setUnmatchedPlayers([])
      setOffset(OFFSET)
    }
  }

  const onGetUnmatchedPlayers = () => {
    if (!isMobile && token)
      getUnmatchedPlayers({
        limit: LIMIT,
        offset: offset,
        bracketId: Number(bracketId),
        query: '*',
        sort: order
          ? {
              order: order,
              parameter: 'RATINGS',
            }
          : null,
      })
    if (bracket) getClubStaff(bracket?.clubId)
  }

  const onConfirmSwitchBracket = () => {
    switchBrackets(switchBracketData, {
      onSuccess: () => {
        closeSwitchBracketModal()
        setUnmatchedPlayers([])
        setSwitchBracketData([])
        onGetUnmatchedPlayers()
        toast.success(
          `The selected teams were successfully move to the following brackets and matches are reseeded accordingly. : ${switchBracketData?.[0].targetBracketName}`
        )
      },
    })
  }

  const onCreateTeam = () => {
    if (bracket) {
      const payload: PayloadBracketCreateTeam = {
        bracketId: bracket.bracketId.toString(),
        clubId: bracket?.clubId.toString(),
        player1: team[0]?.id,
        player2: team[1]?.id,
      }
      bracketCreateTeam(payload, {
        onSuccess: () => {
          closeCreateTeam()
          setUnmatchedPlayers([])
          setTeam([])
          onGetUnmatchedPlayers()
        },
      })
    }
  }

  const handleWithdrawPlayer = () => {
    const payload = {
      bracketId,
      clubId: bracket?.clubId,
      playerId: withdraw?.id,
      ...(withdraw?.registrationDetails?.registrationId && {
        registrationId: withdraw?.registrationDetails?.registrationId,
      }),
    }
    bracketWithdraw(payload, {
      onSuccess: () => {
        closeWithDraw()
        setUnmatchedPlayers([])
        onGetUnmatchedPlayers()
        toast.success(t('common.success'))
      },
      onError: () => {
        closeWithDraw()
        toast.success('Error')
      },
    })
  }

  useEffect(() => {
    onGetUnmatchedPlayers()
  }, [offset, order])

  useEffect(() => {
    if (resUnmatchedPlayers && resBracketSwitchPlayers) {
      const formatPlayers = resUnmatchedPlayers.result.hits.reduce((arr, item) => {
        const findResultSwitch = resBracketSwitchPlayers.results.find((ele) => ele.userId === item.id)
        if (findResultSwitch) {
          arr.push({ ...item, ...(findResultSwitch?.failedReason && { failedReason: findResultSwitch.failedReason }) })
        }
        if (!findResultSwitch) arr.push(item)

        return arr
      }, [] as BracketPlayerClone[])

      setUnmatchedPlayers([...unmatchedPlayers, ...formatPlayers])
    }

    if (resUnmatchedPlayers && !resBracketSwitchPlayers) {
      setUnmatchedPlayers([...unmatchedPlayers, ...resUnmatchedPlayers.result.hits])
    }
  }, [resUnmatchedPlayers, resBracketSwitchPlayers])

  useEffect(() => {
    if (bracket && event) {
      const eligibleBrackets = bracketOption(bracket, switchBracketData, event?.brackets)
      setAvailableBrackets(eligibleBrackets)
    }
  }, [switchBracketData, event, bracket])

  useEffect(() => {
    if (targetBracket) {
      const newData = switchBracketData.map((item: any) => {
        return {
          ...item,
          targetBracketId: targetBracket?.bracketId,
          targetBracketName: targetBracket?.name,
        }
      })
      setSwitchBracketData(newData)
    }
  }, [targetBracket])

  useEffect(() => {
    if (team.length === 2) {
      if (bracket?.playerGroup === 'MIXED') {
        if (team?.[0]?.gender === team?.[1]?.gender) {
          setMixedBracketError(true)
        } else {
          setMixedBracketError(false)
        }

        if (
          team?.[0]?.registrationDetails?.player1?.isWaitListed ||
          team?.[1]?.registrationDetails?.player1?.isWaitListed
        ) {
          setWaitlistBracketError(true)
        } else {
          setWaitlistBracketError(false)
        }
      } else if (
        team?.[0]?.registrationDetails?.player1?.isWaitListed ||
        team?.[1]?.registrationDetails?.player1?.isWaitListed
      ) {
        setWaitlistBracketError(true)
      } else {
        setWaitlistBracketError(false)
      }
    }
  }, [team])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <>
      <div className="flex flex-col gap-y-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-x-4 items-center justify-start">
            <div className="flex gap-x-2 sm:gap-x-4 items-center">
              <Menu as="div" className="relative inline-block">
                <div>
                  <Menu.Button className="flex gap-x-1 items-center bg-primary-500 rounded-full hover:bg-opacity-70 px-3 py-1 text-xs font-semibold text-white">
                    {t('common.sort')}
                    <FilterNoBg className="w-4 h-4" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute z-50 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="px-1 py-1 ">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`${
                              active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            onClick={() => onChangeOrder('DESC')}
                          >
                            <Checkmark className={`${order === 'DESC' ? 'opacity-100' : 'opacity-0'}`} />
                            {t('common.highToLowRating')}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`${
                              active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            onClick={() => onChangeOrder('ASC')}
                          >
                            <Checkmark className={`${order === 'ASC' ? 'opacity-100' : 'opacity-0'}`} />
                            {t('common.lowToHighRating')}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
            <div className="flex items-center">
              <Button
                className="text-xs text-white hidden sm:flex font-semibold hover:text-opacity-70"
                onClick={onClearFilter}
              >
                {t('common.clearFilters')}
              </Button>
              <Button className="flex sm:hidden" onClick={onClearFilter}>
                <Close className="text-white hover:text-opacity-70" />
              </Button>
            </div>
          </div>
          <div className="flex gap-x-2">
            <ButtonPrimary
              title={t('event.switchBracket')}
              disabled={isEmpty(switchBracketData)}
              onClick={() => openSwitchBracketModal('STEP1')}
            />
            <ButtonPrimary
              title={t('event.createTeam')}
              disabled={team.length !== 2}
              onClick={() => {
                openCreateTeam()
              }}
            />
            <div className="flex items-center justify-center" onClick={openSupportModal}>
              <button className="p-1 flex items-center justify-center hover:bg-navy-900 hover:bg-opacity-20 rounded-full">
                <InfoIconOutline className="w-4 h-4 text-navy-900" />
              </button>
            </div>
          </div>
        </div>
        {unmatchedPlayers?.map((item, index) => {
          const isChecked = switchBracketData.find(
            (switchBracketData: any) => switchBracketData.playerId === item.id.toString()
          )

          return index === unmatchedPlayers.length - 1 && resUnmatchedPlayers?.result.hasMore ? (
            <div key={item.id} ref={setLastElement}>
              <BracketPlayerItem
                item={item}
                isChecked={isChecked}
                onChangeCheckbox={() => {
                  updateTeam(item)
                  switchUnmatchedPlayerBracket(item)
                }}
                showCheckBox
                failedReason={item.failedReason}
                isCheckInput
                isShowMore
                organizers={resGetClubStaff?.result.organizers}
                directors={resGetClubStaff?.result.directors}
                onWithdrawRefund={(player, isWithDraw) => {
                  if (player && !isWithDraw) openWithdrawRefund(player)
                  if (player && isWithDraw) openWithdraw(player)
                }}
              />
            </div>
          ) : (
            <BracketPlayerItem
              key={item.id}
              item={item}
              isChecked={isChecked}
              onChangeCheckbox={() => {
                updateTeam(item)
                switchUnmatchedPlayerBracket(item)
              }}
              showCheckBox
              failedReason={item.failedReason}
              isCheckInput
              isShowMore
              organizers={resGetClubStaff?.result.organizers}
              directors={resGetClubStaff?.result.directors}
              onWithdrawRefund={(player, isWithDraw) => {
                if (player && !isWithDraw) openWithdrawRefund(player)
                if (player && isWithDraw) openWithdraw(player)
              }}
            />
          )
        })}

        {isLoading && (
          <>
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
          </>
        )}

        {resUnmatchedPlayers && !resUnmatchedPlayers?.result.hasMore && (
          <div className="flex justify-center">
            <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
          </div>
        )}
      </div>
      <NotifyModal
        open={supportModal || createTeam}
        onClose={() => {
          if (supportModal) closeSupportModal()
          if (createTeam) closeCreateTeam()
        }}
        title={
          supportModal
            ? t('event.createTeam')
            : mixedBracketError
              ? t('event.playersDifferentGenders')
              : waitListError
                ? t('event.unmatchedTeams')
                : t('event.createTeam')
        }
        desc={
          supportModal
            ? t('event.pleaseSelectAnyTwoPlayers')
            : mixedBracketError
              ? t('event.youSelectedTwoPlayersSameGender')
              : waitListError
                ? `${t('event.thisTeamCannotCreated')} ${
                    team?.[0]?.registrationDetails?.player1?.isWaitListed &&
                    team?.[1]?.registrationDetails?.player1?.isWaitListed
                      ? `${team?.[0]?.fullName} ${t('event.and')} ${team?.[1]?.fullName}`
                      : team?.[0]?.registrationDetails?.player1?.isWaitListed
                        ? `${team?.[0]?.fullName}`
                        : team?.[1]?.registrationDetails?.player1?.isWaitListed
                          ? `${team?.[1]?.fullName}`
                          : ''
                  }  ${t('event.isOnTheWaitlist')}`
                : t('event.afterYouConfirm')
        }
        isNotifyConfirm={!supportModal && !mixedBracketError && !waitListError}
        firstDesc={!supportModal && !mixedBracketError && !waitListError ? t('event.youLikeToCreateTeam') : ''}
        submitTitle={
          supportModal ? t('common.ok') : mixedBracketError || waitListError ? t('common.cancel') : t('common.create')
        }
        submitClassName={
          (!supportModal && mixedBracketError) || waitListError ? '!bg-white !text-navy-900 !border !shadow-lg' : ''
        }
        onSubmit={() => {
          if (supportModal) closeSupportModal()
          if (createTeam && (mixedBracketError || waitListError)) closeCreateTeam()
          if (createTeam && !mixedBracketError && !waitListError) {
            onCreateTeam()
          }
        }}
      />
      <SelectBracketModal
        open={switchBracket === 'STEP1'}
        onClose={closeSwitchBracketModal}
        title={t('event.switchBrackets')}
        eligibleBrackets={availableBrackets}
        targetBracket={targetBracket}
        setTargetBracket={setTargetBracket}
        onSubmit={() => setSwitchBracket('STEP2')}
        disabledSubmitBtn={!targetBracket}
      />
      <ConfirmModal
        open={switchBracket === 'STEP2'}
        onClose={() => {
          if (switchBracket === 'STEP2') closeSwitchBracketModal()
        }}
        onCloseButton={() => {
          if (switchBracket === 'STEP2') setSwitchBracket('STEP1')
        }}
        title={switchBracket === 'STEP2' ? t('common.confirmation') : ''}
        desc={switchBracket === 'STEP2' ? t('event.switchBracketsConfirmation') : ''}
        onSubmit={() => {
          if (switchBracket === 'STEP2') onConfirmSwitchBracket()
        }}
        cancelTitle={t('common.back')}
        submitTitle={t('common.confirm')}
      />
      {withdrawRefund && bracket && (
        <WithdrawRefundModal
          open={!!withdrawRefund}
          onClose={closeWithdrawRefund}
          currentPlayerId={withdrawRefund?.id}
          currentEventId={eventId}
          currentPlayer={withdrawRefund?.fullName}
          currentClubId={bracket?.clubId}
          refundCallBack={() => {
            setUnmatchedPlayers([])
            onGetUnmatchedPlayers()
          }}
        />
      )}
      {withdraw && (
        <WithdrawModal
          title={t('event.withdrawPlayer')}
          open={!!withdraw}
          onClose={closeWithDraw}
          rightButton={handleWithdrawPlayer}
          content={
            <p className="text-sm text-navy-900 text-opacity-70">
              {t('event.confirmWithdrawPlayerFirstText')} <strong>{withdraw?.fullName}</strong>{' '}
              {t('event.confirmWithdrawPlayerSecondText')}
            </p>
          }
        />
      )}
    </>
  )
}
